import { fetchRecipes } from './ajax.js';
import { assignRecipeStore } from './store.js';
import { templateStore } from '../../template/index.js';

// ─── Shared state ─────────────────────────────────────────────────────────────
export let recipeListContainer = null;
export let searchInput = null;
export let currentPage = 1;
export let isLoading = false;
export let hasMore = true;
export let searchQuery = '';

export function setRecipeListContainer(el) {
	recipeListContainer = el;
}
export function setSearchInput(el) {
	searchInput = el;
}
export function setCurrentPage(v) {
	currentPage = v;
}
export function setIsLoading(v) {
	isLoading = v;
}
export function setHasMore(v) {
	hasMore = v;
}
export function setSearchQuery(v) {
	searchQuery = v;
}

// ─── Core load logic ──────────────────────────────────────────────────────────

/**
 * Reset pagination state and reload from page 1
 */
export async function resetAndLoad() {
	currentPage = 1;
	hasMore = true;
	if (recipeListContainer) recipeListContainer.innerHTML = '';
	await loadRecipes();
}

/**
 * Load a page of recipes and append to the list
 */
export async function loadRecipes() {
	if (isLoading || !hasMore) return;
	isLoading = true;

	appendSkeletons(3);

	const result = await fetchRecipes({ search: searchQuery, page: currentPage });

	removeSkeletons();

	if (!result) {
		appendError();
		isLoading = false;
		return;
	}

	if (result.recipes.length === 0 && currentPage === 1) {
		appendEmpty();
		isLoading = false;
		return;
	}

	const template = templateStore.get('stage/recipe/select-item.ejs');

	result.recipes.forEach((recipe) => {
		// eslint-disable-next-line no-undef
		const html = ejs.render(template, { recipe });
		recipeListContainer.insertAdjacentHTML('beforeend', html);
	});

	syncCheckboxState();

	hasMore = result.has_more;
	currentPage++;
	isLoading = false;
}

/**
 * Sync checkbox checked state with store for all visible items
 */
export function syncCheckboxState() {
	if (!recipeListContainer) return;
	recipeListContainer
		.querySelectorAll('.recipe-item input[type="checkbox"]')
		.forEach((checkbox) => {
			checkbox.checked = assignRecipeStore.isSelected(checkbox.value);
		});
}

// ─── Skeleton / empty / error helpers ────────────────────────────────────────

export function appendSkeletons(count) {
	for (let i = 0; i < count; i++) {
		const el = document.createElement('div');
		el.className = 'recipe-skeleton p-3 sm:p-4 rounded-lg bg-white animate-pulse';
		el.innerHTML = `
			<div class="flex items-start gap-3 sm:gap-4 h-full">
				<div class="w-5 h-5 rounded bg-gray-200 shrink-0 mt-0.5"></div>
				<div class="flex flex-col sm:flex-row gap-3 sm:gap-4 flex-1 min-w-0">
					<div class="flex gap-3 sm:gap-4 shrink-0">
						<div class="h-20 w-20 sm:h-40 sm:w-40 rounded-lg bg-gray-200 shrink-0"></div>
						<div class="sm:hidden flex flex-col gap-2 flex-1 min-w-0 pt-1">
							<div class="h-3.5 bg-gray-200 rounded w-3/4"></div>
							<div class="h-3 bg-gray-200 rounded w-1/2"></div>
						</div>
					</div>
					<div class="flex flex-col gap-3 flex-1 min-w-0">
						<div class="h-4 bg-gray-200 rounded w-2/3 hidden sm:block"></div>
						<div class="grid grid-cols-2 gap-2 sm:gap-4 w-full">
							${Array.from({ length: 4 }, () => `
							<div class="flex items-center gap-2">
								<div class="bg-gray-200 h-10 w-10 rounded-lg shrink-0"></div>
								<div class="flex flex-col gap-1 flex-1 min-w-0">
									<div class="h-2.5 bg-gray-200 rounded w-2/3"></div>
									<div class="h-3 bg-gray-200 rounded w-1/2"></div>
								</div>
							</div>`).join('')}
						</div>
					</div>
				</div>
			</div>`;
		recipeListContainer.appendChild(el);
	}
}

export function removeSkeletons() {
	recipeListContainer?.querySelectorAll('.recipe-skeleton').forEach((el) => el.remove());
}

export function appendEmpty() {
	recipeListContainer.innerHTML = `<div class="text-center py-8 text-gray-400 text-sm">${wpcore.direct_t({ en: 'No recipes found', fr: 'Aucune recette trouvée' })}</div>`;
}

export function appendError() {
	recipeListContainer.innerHTML = `<div class="text-center py-8 text-red-400 text-sm">${wpcore.direct_t({ en: 'Error loading recipes', fr: 'Erreur lors du chargement' })}</div>`;
}

// ─── Init ─────────────────────────────────────────────────────────────────────
import {
	handlePopupOpen,
	handleScroll,
	handleCheckboxChange,
	handleFormSubmit,
} from './handlers.js';

export function initSelectForm() {
	const form = document.getElementById('select-recipes-form');
	if (!form) return;

	recipeListContainer = form.querySelector('.recipe-list');
	searchInput = form.querySelector('.recipe-search-input');

	if (searchInput) {
		let debounceTimer = null;
		searchInput.addEventListener('input', (e) => {
			clearTimeout(debounceTimer);
			debounceTimer = setTimeout(() => {
				searchQuery = e.target.value.trim();
				resetAndLoad();
			}, 350);
		});
	}

	if (recipeListContainer) {
		recipeListContainer.addEventListener('scroll', handleScroll);
		recipeListContainer.addEventListener('change', handleCheckboxChange);
	}

	document.addEventListener('popup-opened', handlePopupOpen);
	form.addEventListener('submit', handleFormSubmit);
}
